import { $t } from '../../composables/i18n'
import { Codebook } from '../Codebook'
import { Color } from '../color/Color'

export enum SalesCaseChangeLogTypeId {
  SALES_CASE_CREATED = 26,
  SALES_CASE_NAME_CHANGED = 27,
  SALES_CASE_ACTIVITY_SCHEDULED = 28,
  SALES_CASE_ACTIVITY_COMPLETED = 29,
  SALES_CASE_ACTIVITY_RESCHEDULED = 30,
  SALES_CASE_ACTIVITY_CANCELLED = 31,
  SALES_CASE_CONVERTED_TO_DEAL = 32,
  SALES_CASE_OFFER_CREATED = 33,
  SALES_CASE_STARTED_IMPLEMENTATION = 34,
  SALES_CASE_IMPLEMENTATION_FINISHED = 35,
  SALES_CASE_STATUS_CHANGED = 36,
  SALES_CASE_POTENTIAL_CHANGED = 37,
  SALES_CASE_DISQUALIFIED = 38,
  SALES_CASE_MARKED_AS_LOST = 39,
  SALES_CASE_PROPERTY_LINKED = 40,
  SALES_CASE_PROPERTY_UNLINKED = 41,
  SALES_CASE_PROPERTY_REPLACED = 42,
  SALES_CASE_CONTACT_PERSON_LINKED = 43,
  SALES_CASE_CONTACT_PERSON_UNLINKED = 44,
  SALES_CASE_REOPENED = 45,

  SALES_CASE_NOTE_ADDED = 46,
  SALES_CASE_NOTE_RENAMED = 47,
  SALES_CASE_NOTE_EDITED = 48,
  SALES_CASE_NOTE_REMOVED = 49,

  SALES_CASE_ACTIVITY_INCOMPLETED = 50,
  SALES_CASE_ACTIVITY_UPDATED = 51,

  LEAD_TAG_UPDATED = 52,
  SALES_CASE_CONVERTED_TO_LEAD = 53,
  SALES_CASE_SALESMAN_CHANGED = 54,
  SALES_CASE_MERGED = 55,

  OFFER_CREATED = 56,

  SALES_CASE_IMPLEMENTATION_CONTINUED = 62,

  OFFER_UPDATED = 63,
  SALES_CASE_OFFER_DELETED = 64,
  OFFER_SENT = 65,
  OFFER_SIGNED = 66,
  OFFER_REMINDER_SENT = 68,
}

export class SalesCaseChangeLogType extends Codebook<SalesCaseChangeLogTypeId> {
  private constructor(
    id: SalesCaseChangeLogTypeId,
    public readonly name: () => string,
    public readonly icon: string,
    public readonly color: Color,
  ) {
    super(id)
  }

  static readonly SALES_CASE_CREATED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_CREATED,
    () => $t('Sales case created'),
    'plus',
    Color.POSITIVE,
  )

  static readonly SALES_CASE_NAME_CHANGED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_NAME_CHANGED,
    () => $t('Name changed'),
    'pen',
    Color.PRIMARY,
  )

  static readonly SALES_CASE_ACTIVITY_SCHEDULED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_ACTIVITY_SCHEDULED,
    () => $t('Activity scheduled'),
    'calendar',
    Color.PRIMARY,
  )

  static readonly SALES_CASE_ACTIVITY_COMPLETED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_ACTIVITY_COMPLETED,
    () => $t('Activity completed'),
    'calendar-check',
    Color.POSITIVE,
  )

  static readonly SALES_CASE_ACTIVITY_RESCHEDULED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_ACTIVITY_RESCHEDULED,
    () => $t('Activity rescheduled'),
    'calendar',
    Color.WARNING,
  )

  static readonly SALES_CASE_ACTIVITY_CANCELLED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_ACTIVITY_CANCELLED,
    () => $t('Activity cancelled'),
    'calendar-xmark',
    Color.NEGATIVE,
  )

  static readonly SALES_CASE_CONVERTED_TO_DEAL = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_CONVERTED_TO_DEAL,
    () => $t('Converted to deal'),
    'handshake',
    Color.POSITIVE,
  )

  static readonly SALES_CASE_OFFER_CREATED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_OFFER_CREATED,
    () => $t('Offer created'),
    'hand-holding-dollar',
    Color.PRIMARY,
  )

  static readonly SALES_CASE_OFFER_DELETED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_OFFER_DELETED,
    () => $t('Offer deleted'),
    'hand-holding-dollar',
    Color.NEGATIVE,
  )

  static readonly SALES_CASE_STARTED_IMPLEMENTATION = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_STARTED_IMPLEMENTATION,
    () => $t('Implementation started'),
    'rocket',
    Color.PRIMARY,
  )

  static readonly SALES_CASE_IMPLEMENTATION_CONTINUED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_IMPLEMENTATION_CONTINUED,
    () => $t('Implementation continued'),
    'rocket',
    Color.PRIMARY,
  )

  static readonly SALES_CASE_IMPLEMENTATION_FINISHED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_IMPLEMENTATION_FINISHED,
    () => $t('Implementation finished'),
    'circle-check',
    Color.POSITIVE,
  )

  static readonly SALES_CASE_STATUS_CHANGED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_STATUS_CHANGED,
    () => $t('Status changed'),
    'arrow-right-arrow-left',
    Color.PRIMARY,
  )

  static readonly SALES_CASE_POTENTIAL_CHANGED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_POTENTIAL_CHANGED,
    () => $t('Potential changed'),
    'chart-simple',
    Color.PRIMARY,
  )

  static readonly SALES_CASE_DISQUALIFIED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_DISQUALIFIED,
    () => $t('Disqualified'),
    'ban',
    Color.NEGATIVE,
  )

  static readonly SALES_CASE_MARKED_AS_LOST = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_MARKED_AS_LOST,
    () => $t('Marked as lost'),
    'xmark',
    Color.NEGATIVE,
  )

  static readonly SALES_CASE_PROPERTY_LINKED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_PROPERTY_LINKED,
    () => $t('Property linked'),
    'building',
    Color.POSITIVE,
  )

  static readonly SALES_CASE_PROPERTY_UNLINKED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_PROPERTY_UNLINKED,
    () => $t('Property unlinked'),
    'building',
    Color.NEGATIVE,
  )

  static readonly SALES_CASE_PROPERTY_REPLACED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_PROPERTY_REPLACED,
    () => $t('Property replaced'),
    'building',
    Color.WARNING,
  )

  static readonly SALES_CASE_CONTACT_PERSON_LINKED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_CONTACT_PERSON_LINKED,
    () => $t('Contact person linked'),
    'user',
    Color.POSITIVE,
  )

  static readonly SALES_CASE_CONTACT_PERSON_UNLINKED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_CONTACT_PERSON_UNLINKED,
    () => $t('Contact person unlinked'),
    'user',
    Color.NEGATIVE,
  )

  static readonly SALES_CASE_REOPENED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_REOPENED,
    () => $t('Reopened'),
    'rotate-left',
    Color.PRIMARY,
  )

  static readonly SALES_CASE_NOTE_ADDED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_NOTE_ADDED,
    () => $t('Note added'),
    'note-sticky',
    Color.PRIMARY,
  )

  static readonly SALES_CASE_NOTE_RENAMED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_NOTE_RENAMED,
    () => $t('Note renamed'),
    'note-sticky',
    Color.PRIMARY,
  )

  static readonly SALES_CASE_NOTE_EDITED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_NOTE_EDITED,
    () => $t('Note edited'),
    'note-sticky',
    Color.PRIMARY,
  )

  static readonly SALES_CASE_NOTE_REMOVED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_NOTE_REMOVED,
    () => $t('Note removed'),
    'note-sticky',
    Color.NEGATIVE,
  )

  static readonly SALES_CASE_ACTIVITY_INCOMPLETED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_ACTIVITY_INCOMPLETED,
    () => $t('Activity incompleted'),
    'calendar',
    Color.WARNING,
  )

  static readonly SALES_CASE_ACTIVITY_UPDATED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_ACTIVITY_UPDATED,
    () => $t('Activity updated'),
    'calendar',
    Color.PRIMARY,
  )

  static readonly LEAD_TAG_UPDATED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.LEAD_TAG_UPDATED,
    () => $t('Tag updated'),
    'tag',
    Color.PRIMARY,
  )

  static readonly SALES_CASE_CONVERTED_TO_LEAD = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_CONVERTED_TO_LEAD,
    () => $t('Converted to lead'),
    'rotate-left',
    Color.WARNING,
  )

  static readonly SALES_CASE_SALESMAN_CHANGED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_SALESMAN_CHANGED,
    () => $t('Salesman changed'),
    'user',
    Color.PRIMARY,
  )

  static readonly SALES_CASE_MERGED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.SALES_CASE_MERGED,
    () => $t('Sales cases merged'),
    'code-merge',
    Color.PRIMARY,
  )

  static readonly OFFER_CREATED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.OFFER_CREATED,
    () => $t('Offer created'),
    'hand-holding-dollar',
    Color.PRIMARY,
  )

  static readonly OFFER_UPDATED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.OFFER_UPDATED,
    () => $t('Offer updated'),
    'hand-holding-dollar',
    Color.PRIMARY,
  )

  static readonly OFFER_SENT = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.OFFER_SENT,
    () => $t('Offer sent to client'),
    'paper-plane',
    Color.PRIMARY,
  )

  static readonly OFFER_SIGNED = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.OFFER_SIGNED,
    () => $t('Offer signed by client'),
    'file-signature',
    Color.POSITIVE,
  )

  static readonly OFFER_REMINDER_SENT = new SalesCaseChangeLogType(
    SalesCaseChangeLogTypeId.OFFER_REMINDER_SENT,
    () => $t('Offer expiration reminder sent to client'),
    'bell',
    Color.PRIMARY,
  )
}
